import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Calendar,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  ChevronLeft,
  Users,
  Building,
  Eye,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Link, useLocation } from "wouter";

interface TimeOffRequest {
  request: {
    id: number;
    employeeId: number;
    policyId: number;
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
    totalHours: string;
    requestType: string;
    status: string;
    reason?: string;
    denialReason?: string;
    requestedAt: string;
    reviewedAt?: string;
    reviewedBy?: string;
    notes?: string;
  };
  policy: {
    id: number;
    name: string;
    color?: string;
  } | null;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
  } | null;
  canApprove?: boolean;
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800",
  approved: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
  denied: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
  cancelled: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
  withdrawn: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
};

export default function TimeOffRequests() {
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [policyFilter, setPolicyFilter] = useState<string>("all");
  const [scope, setScope] = useState<"all" | "team">("all");
  const [selectedRequest, setSelectedRequest] = useState<TimeOffRequest | null>(null);
  const [actionType, setActionType] = useState<"approve" | "deny" | null>(null);
  const [denialReason, setDenialReason] = useState("");
  const [approvalNotes, setApprovalNotes] = useState("");

  const { data: requests, isLoading } = useQuery<TimeOffRequest[]>({
    queryKey: ["/api/time-off/requests", scope],
    queryFn: async () => {
      const params = scope === "team" ? "?scope=team" : "";
      const res = await fetch(`/api/time-off/requests${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch requests");
      return res.json();
    },
  });

  const { data: policies } = useQuery<any[]>({
    queryKey: ["/api/time-off/policies"],
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes?: string }) => {
      return apiRequest("PATCH", `/api/time-off/requests/${id}/approve`, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/metrics"] });
      toast({ title: "Request approved", description: "The time off request has been approved." });
      setSelectedRequest(null);
      setActionType(null);
      setApprovalNotes("");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to approve request.", variant: "destructive" });
    },
  });

  const denyMutation = useMutation({
    mutationFn: async ({ id, denialReason }: { id: number; denialReason: string }) => {
      return apiRequest("PATCH", `/api/time-off/requests/${id}/deny`, { denialReason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/metrics"] });
      toast({ title: "Request denied", description: "The time off request has been denied." });
      setSelectedRequest(null);
      setActionType(null);
      setDenialReason("");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to deny request.", variant: "destructive" });
    },
  });

  const filteredRequests = useMemo(() => {
    if (!requests) return [];
    
    return requests.filter((req) => {
      const matchesSearch =
        !searchTerm ||
        `${req.employee?.firstName} ${req.employee?.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        req.policy?.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === "all" || req.request.status === statusFilter;
      const matchesPolicy = policyFilter === "all" || req.request.policyId.toString() === policyFilter;

      return matchesSearch && matchesStatus && matchesPolicy;
    });
  }, [requests, searchTerm, statusFilter, policyFilter]);

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (start === end) {
      return format(startDate, "MMM d, yyyy");
    }
    return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`;
  };

  const handleReview = (req: TimeOffRequest) => {
    setSelectedRequest(req);
    setActionType(null);
    setApprovalNotes("");
    setDenialReason("");
  };

  const confirmAction = () => {
    if (!selectedRequest) return;

    if (actionType === "approve") {
      approveMutation.mutate({ id: selectedRequest.request.id, notes: approvalNotes });
    } else if (actionType === "deny") {
      if (!denialReason.trim()) {
        toast({ title: "Reason required", description: "Please provide a reason for denial.", variant: "destructive" });
        return;
      }
      denyMutation.mutate({ id: selectedRequest.request.id, denialReason });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link href="/time-off">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Time Off Requests</h1>
          <p className="text-gray-500">Review and manage employee time off requests</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant={scope === "team" ? "default" : "outline"}
                size="sm"
                onClick={() => setScope("team")}
                className="flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                My Team
              </Button>
              <Button
                variant={scope === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setScope("all")}
                className="flex items-center gap-2"
              >
                <Building className="h-4 w-4" />
                All Company
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by employee or policy..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="denied">Denied</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={policyFilter} onValueChange={setPolicyFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Policy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Policies</SelectItem>
                    {policies?.map((policy) => (
                      <SelectItem key={policy.id} value={policy.id.toString()}>
                        {policy.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              <p>No time off requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-gray-800">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Employee
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Dates
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Hours
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Requested
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((req) => (
                    <tr key={req.request.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-3 px-4">
                        <div className="font-medium">
                          {req.employee?.firstName} {req.employee?.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{req.employee?.email}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: req.policy?.color || "#6B7280" }}
                          />
                          {req.policy?.name}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {formatDateRange(req.request.startDate, req.request.endDate)}
                      </td>
                      <td className="py-3 px-4">{req.request.totalHours}h</td>
                      <td className="py-3 px-4">
                        <Badge
                          variant="outline"
                          className={statusColors[req.request.status] || statusColors.pending}
                        >
                          {req.request.status.charAt(0).toUpperCase() + req.request.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {req.request.requestedAt
                          ? format(new Date(req.request.requestedAt), "MMM d, yyyy")
                          : "-"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          {req.request.status === "pending" ? (
                            req.canApprove !== false ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReview(req)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                            ) : (
                              <span className="text-xs text-gray-400 italic">
                                Not authorized
                              </span>
                            )
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReview(req)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Details
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedRequest} onOpenChange={() => { setSelectedRequest(null); setActionType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve"
                ? "Confirm Approval"
                : actionType === "deny"
                ? "Confirm Denial"
                : selectedRequest?.request.status === "pending"
                ? "Review Request"
                : "Request Details"}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-2">
                <p>
                  <strong>Employee:</strong> {selectedRequest.employee?.firstName}{" "}
                  {selectedRequest.employee?.lastName}
                </p>
                <p>
                  <strong>Type:</strong> {selectedRequest.policy?.name}
                </p>
                <p>
                  <strong>Dates:</strong>{" "}
                  {formatDateRange(selectedRequest.request.startDate, selectedRequest.request.endDate)}
                </p>
                <p>
                  <strong>Hours:</strong> {selectedRequest.request.totalHours}h
                </p>
                {selectedRequest.request.reason && (
                  <p>
                    <strong>Reason:</strong> {selectedRequest.request.reason}
                  </p>
                )}
                <p>
                  <strong>Requested:</strong>{" "}
                  {selectedRequest.request.requestedAt
                    ? format(new Date(selectedRequest.request.requestedAt), "MMM d, yyyy")
                    : "-"}
                </p>
                {selectedRequest.request.status !== "pending" && (
                  <>
                    <p>
                      <strong>Status:</strong>{" "}
                      <Badge
                        variant="outline"
                        className={statusColors[selectedRequest.request.status] || statusColors.pending}
                      >
                        {selectedRequest.request.status.charAt(0).toUpperCase() + selectedRequest.request.status.slice(1)}
                      </Badge>
                    </p>
                    {selectedRequest.request.reviewedAt && (
                      <p>
                        <strong>Reviewed:</strong>{" "}
                        {format(new Date(selectedRequest.request.reviewedAt), "MMM d, yyyy")}
                      </p>
                    )}
                    {selectedRequest.request.denialReason && (
                      <p>
                        <strong>Denial Reason:</strong> {selectedRequest.request.denialReason}
                      </p>
                    )}
                  </>
                )}
              </div>

              {actionType === "approve" && (
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add any notes for this approval..."
                  />
                </div>
              )}

              {actionType === "deny" && (
                <div>
                  <Label>
                    Reason for Denial <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    value={denialReason}
                    onChange={(e) => setDenialReason(e.target.value)}
                    placeholder="Please provide a reason for denying this request..."
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {actionType ? (
              <>
                <Button variant="outline" onClick={() => setActionType(null)}>
                  Back
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setSelectedRequest(null); setActionType(null); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmAction}
                  disabled={approveMutation.isPending || denyMutation.isPending}
                  className={actionType === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
                >
                  {approveMutation.isPending || denyMutation.isPending
                    ? "Processing..."
                    : actionType === "approve"
                    ? "Confirm Approval"
                    : "Confirm Denial"}
                </Button>
              </>
            ) : selectedRequest?.request.status === "pending" && selectedRequest?.canApprove !== false ? (
              <>
                <Button
                  variant="outline"
                  className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                  onClick={() => setActionType("approve")}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                  onClick={() => setActionType("deny")}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Deny
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
